/**
 * ===========================================================================
 *  music.js — the adaptive score
 * ===========================================================================
 * Heavy rock over orchestral percussion, which is the Dynasty Warriors sound:
 * a driving kit, a distorted power-chord guitar bed, an overdriven bass, taiko
 * hits on the halves, sustained brass/pad harmony and a pentatonic lead that
 * shreds when the battle heats up.
 *
 * It is SEQUENCED, not looped: a 16th-note grid is scheduled ahead against
 * `AudioContext.currentTime` (never setTimeout — a timer would drift and every
 * hit would land late), and INTENSITY only moves layer gains. The grid, the
 * tempo and the harmony never stop, so a transition from exploring to a full
 * battle is a crossfade that lands on a bar line, not a cut.
 *
 * States: silence / explore / skirmish / battle / duel / musou / victory / defeat
 * ===========================================================================
 */
import { env, envASR, sweep, gainNode, filter, noiseShot, shaperCurve, noteHz } from './synth.js';

const BPM = 132;
const BEAT = 60 / BPM;
const STEP = BEAT / 4;              // one 16th
const BAR = BEAT * 4;
const LOOKAHEAD = 0.38;             // seconds of grid scheduled in advance

/* Chord roots in semitones relative to A2 (110 Hz), one per bar, plus quality.
 * MAIN is Am Am F G | Am Am F E — minor with a dominant lift into the repeat. */
const PROG = {
  main: [[0, 'm'], [0, 'm'], [-4, 'M'], [-2, 'M'], [0, 'm'], [0, 'm'], [-4, 'M'], [-5, 'M']],
  drive: [[0, 'm'], [-2, 'M'], [-4, 'M'], [-5, 'M'], [0, 'm'], [-2, 'M'], [3, 'M'], [-2, 'M']],
  victory: [[0, 'M'], [5, 'M'], [7, 'M'], [0, 'M'], [0, 'M'], [5, 'M'], [7, 'M'], [0, 'M']],
  defeat: [[0, 'm'], [0, 'm'], [-4, 'M'], [-4, 'M'], [-5, 'M'], [-5, 'M'], [0, 'm'], [0, 'm']],
};

const PENT_MIN = [0, 3, 5, 7, 10, 12, 15, 17, 19, 22, 24];
const PENT_MAJ = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21, 24];

/* Drum patterns, 16 steps per bar. 0 = silent, 1..3 = velocity tier. */
const PAT = {
  none: { kick: [], snare: [], hat: [], taiko: [] },
  light: {
    kick: [0, 8], snare: [4, 12], hat: [0, 2, 4, 6, 8, 10, 12, 14], taiko: [0],
  },
  mid: {
    kick: [0, 3, 6, 8, 11, 14], snare: [4, 12], hat: [0, 2, 4, 6, 8, 10, 12, 14], taiko: [0, 8],
  },
  heavy: {
    kick: [0, 3, 6, 8, 10, 11, 14], snare: [4, 12, 15], hat: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    taiko: [0, 6, 8, 14],
  },
  duel: {
    kick: [0, 2, 3, 6, 8, 10, 11, 14], snare: [4, 7, 12, 15], hat: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    taiko: [0, 4, 8, 12],
  },
  musou: {
    kick: [0, 2, 4, 6, 8, 10, 12, 14], snare: [4, 6, 12, 14], hat: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    taiko: [0, 2, 4, 6, 8, 10, 12, 14],
  },
  fanfare: { kick: [0, 6, 8, 14], snare: [4, 12], hat: [0, 4, 8, 12], taiko: [0, 4, 8, 12] },
};

/* Layer gain targets per state. Layers: drums taiko bass guitar lead pad horns erhu */
export const STATES = {
  silence: { pat: 'none', prog: 'main', L: { drums: 0, taiko: 0, bass: 0, guitar: 0, lead: 0, pad: 0, horns: 0, erhu: 0 } },
  explore: { pat: 'light', prog: 'main', L: { drums: 0.0, taiko: 0.22, bass: 0.30, guitar: 0.0, lead: 0.0, pad: 0.62, horns: 0.10, erhu: 0.55 }, sparse: 2 },
  skirmish: { pat: 'light', prog: 'main', L: { drums: 0.52, taiko: 0.38, bass: 0.60, guitar: 0.26, lead: 0.14, pad: 0.42, horns: 0.18, erhu: 0.18 } },
  battle: { pat: 'mid', prog: 'main', L: { drums: 0.85, taiko: 0.55, bass: 0.82, guitar: 0.72, lead: 0.46, pad: 0.34, horns: 0.36, erhu: 0 } },
  heavy: { pat: 'heavy', prog: 'drive', L: { drums: 1.0, taiko: 0.70, bass: 0.95, guitar: 0.95, lead: 0.68, pad: 0.30, horns: 0.48, erhu: 0 } },
  duel: { pat: 'duel', prog: 'drive', L: { drums: 1.0, taiko: 0.78, bass: 1.0, guitar: 1.0, lead: 0.82, pad: 0.26, horns: 0.62, erhu: 0 } },
  musou: { pat: 'musou', prog: 'drive', L: { drums: 1.0, taiko: 1.0, bass: 1.0, guitar: 1.0, lead: 1.0, pad: 0.40, horns: 0.85, erhu: 0 }, octave: 12 },
  victory: { pat: 'fanfare', prog: 'victory', L: { drums: 0.5, taiko: 0.85, bass: 0.7, guitar: 0.45, lead: 0.6, pad: 0.7, horns: 1.0, erhu: 0.2 }, major: true },
  defeat: { pat: 'none', prog: 'defeat', L: { drums: 0, taiko: 0.18, bass: 0.25, guitar: 0, lead: 0, pad: 0.7, horns: 0.25, erhu: 0.4 }, sparse: 4 },
};

export const MUSIC_STATES = Object.keys(STATES);

export function createMusic(graph, rnd) {
  const ctx = graph.ctx;
  const out = graph.buses.music.in;
  const NAMES = ['drums', 'taiko', 'bass', 'guitar', 'lead', 'pad', 'horns', 'erhu'];

  /* ---------------------------------------------------------- layer bus -- */
  const L = {};
  for (const n of NAMES) { const g = gainNode(ctx, 0); g.connect(out); L[n] = g; }

  // guitar amp: pre-gain -> fuzz -> cabinet band -> layer
  const gtrPre = gainNode(ctx, 3.4);
  const gtrShape = ctx.createWaveShaper(); gtrShape.curve = shaperCurve(11); gtrShape.oversample = '4x';
  const gtrCabLo = filter(ctx, 'highpass', 130, 0.7);
  const gtrCabHi = filter(ctx, 'lowpass', 3400, 0.9);
  const gtrPres = filter(ctx, 'peaking', 2100, 1.2); gtrPres.gain.value = 5;
  gtrPre.connect(gtrShape); gtrShape.connect(gtrCabLo); gtrCabLo.connect(gtrPres);
  gtrPres.connect(gtrCabHi); gtrCabHi.connect(L.guitar);

  // bass overdrive
  const bassPre = gainNode(ctx, 1.5);
  const bassShape = ctx.createWaveShaper(); bassShape.curve = shaperCurve(2.6);
  const bassTone = filter(ctx, 'lowpass', 1500, 0.8);
  bassPre.connect(bassShape); bassShape.connect(bassTone); bassTone.connect(L.bass);

  // lead delay (dotted 8th) + a little reverb
  const leadIn = gainNode(ctx, 1);
  const dly = ctx.createDelay(1.0); dly.delayTime.value = BEAT * 0.75;
  const fb = gainNode(ctx, 0.34);
  const dlyTone = filter(ctx, 'lowpass', 3200, 0.7);
  leadIn.connect(L.lead); leadIn.connect(dly);
  dly.connect(dlyTone); dlyTone.connect(fb); fb.connect(dly); dlyTone.connect(L.lead);
  const padSend = gainNode(ctx, 0.35);
  L.pad.connect(padSend); padSend.connect(graph.verbIn);
  const hornSend = gainNode(ctx, 0.3);
  L.horns.connect(hornSend); hornSend.connect(graph.verbIn);

  /* ------------------------------------------------------------- motifs -- */
  /* One 8-bar lead melody, generated once from the deterministic RNG: a
   * contour walk over the pentatonic with a couple of held notes so it phrases
   * instead of running scales. */
  function makeMotif(scale) {
    const notes = [];
    let deg = 4;
    for (let bar = 0; bar < 8; bar++) {
      let s = 0;
      while (s < 16) {
        const long = rnd() < 0.22;
        const len = long ? (rnd() < 0.5 ? 8 : 6) : (rnd() < 0.65 ? 2 : 4);
        if (rnd() < (bar % 4 === 3 ? 0.42 : 0.16)) { s += len; continue; }  // rests, more at phrase ends
        deg += Math.round((rnd() * 2 - 1) * 2.4);
        if (deg < 0) deg = 1; if (deg > scale.length - 1) deg = scale.length - 2;
        notes.push({ bar, step: s, deg, len: Math.min(len, 16 - s), vel: long ? 1 : 0.8 + rnd() * 0.2 });
        s += len;
      }
    }
    return notes;
  }
  const motifMinor = makeMotif(PENT_MIN);
  const motifMajor = makeMotif(PENT_MAJ);
  /* Sparse erhu-ish line for the exploring state: long, few, vibrato'd. */
  const erhuLine = [];
  for (let bar = 0; bar < 8; bar++) {
    if (rnd() < 0.30) continue;
    const s = [0, 4, 8][Math.floor(rnd() * 3)];
    erhuLine.push({ bar, step: s, deg: 2 + Math.floor(rnd() * 5), len: 8 + Math.floor(rnd() * 8) });
  }

  /* --------------------------------------------------------- instruments -- */
  const n = graph.noise;

  function kick(t, v) {
    const o = ctx.createOscillator(); o.type = 'sine';
    sweep(o.frequency, t, 150, 44, 0.09);
    const g = gainNode(ctx, 0); env(g.gain, t, 0.85 * v, 0.002, 0.24);
    o.connect(g); g.connect(L.drums); o.start(t); o.stop(t + 0.34);
    const c = filter(ctx, 'highpass', 1600, 0.7); const cg = gainNode(ctx, 0);
    env(cg.gain, t, 0.16 * v, 0.0005, 0.012);
    const s = noiseShot(ctx, n.white, t, 0.03, rnd); s.connect(c); c.connect(cg); cg.connect(L.drums); s.stop(t + 0.05);
  }
  function snare(t, v) {
    const bp = filter(ctx, 'bandpass', 1900, 0.8); const g = gainNode(ctx, 0);
    env(g.gain, t, 0.42 * v, 0.001, 0.17);
    const s = noiseShot(ctx, n.white, t, 0.24, rnd); s.connect(bp); bp.connect(g); g.connect(L.drums); s.stop(t + 0.26);
    for (let i = 0; i < 2; i++) {
      const o = ctx.createOscillator(); o.type = 'triangle';
      o.frequency.setValueAtTime(i ? 246 : 182, t);
      const og = gainNode(ctx, 0); env(og.gain, t, 0.18 * v, 0.001, 0.09);
      o.connect(og); og.connect(L.drums); o.start(t); o.stop(t + 0.14);
    }
  }
  function hat(t, v, open) {
    const hp = filter(ctx, 'highpass', 7600, 0.7); const g = gainNode(ctx, 0);
    env(g.gain, t, 0.10 * v, 0.0006, open ? 0.22 : 0.036);
    const s = noiseShot(ctx, n.white, t, open ? 0.3 : 0.06, rnd);
    s.connect(hp); hp.connect(g); g.connect(L.drums); s.stop(t + (open ? 0.34 : 0.09));
  }
  function crash(t, v) {
    const hp = filter(ctx, 'highpass', 3600, 0.6); const g = gainNode(ctx, 0);
    env(g.gain, t, 0.22 * v, 0.002, 1.5);
    const s = noiseShot(ctx, n.white, t, 1.6, rnd);
    s.connect(hp); hp.connect(g); g.connect(L.drums); s.stop(t + 1.7);
    const sd = gainNode(ctx, 0.5); g.connect(sd); sd.connect(graph.verbIn);
  }
  function taikoHit(t, v) {
    const o = ctx.createOscillator(); o.type = 'sine';
    sweep(o.frequency, t, 104 * (0.97 + rnd() * 0.06), 60, 0.16);
    const g = gainNode(ctx, 0); env(g.gain, t, 0.75 * v, 0.003, 0.5);
    o.connect(g); g.connect(L.taiko); o.start(t); o.stop(t + 0.62);
    const bp = filter(ctx, 'bandpass', 400, 1.3); const sg = gainNode(ctx, 0);
    env(sg.gain, t, 0.26 * v, 0.001, 0.18);
    const s = noiseShot(ctx, n.pink, t, 0.22, rnd); s.connect(bp); bp.connect(sg); sg.connect(L.taiko); s.stop(t + 0.24);
    const sd = gainNode(ctx, 0.28); g.connect(sd); sd.connect(graph.verbIn);
  }
  function bassNote(t, semi, dur, v) {
    const f = noteHz(semi, 55);
    const o = ctx.createOscillator(); o.type = 'sawtooth'; o.frequency.setValueAtTime(f, t);
    const o2 = ctx.createOscillator(); o2.type = 'sine'; o2.frequency.setValueAtTime(f, t);
    const lp = filter(ctx, 'lowpass', 240, 4);
    sweep(lp.frequency, t, 1500, 420, Math.min(0.18, dur));
    const g = gainNode(ctx, 0); envASR(g.gain, t, 0.5 * v, 0.006, dur * 0.6, dur * 0.5);
    o.connect(lp); lp.connect(g);
    const g2 = gainNode(ctx, 0); envASR(g2.gain, t, 0.42 * v, 0.006, dur * 0.6, dur * 0.5);
    o2.connect(g2); g2.connect(bassPre); g.connect(bassPre);
    o.start(t); o.stop(t + dur + 0.2); o2.start(t); o2.stop(t + dur + 0.2);
  }
  function powerChord(t, root, dur, v, third) {
    const semis = third === null ? [0, 7, 12] : [0, 7, 12, 19];
    const g = gainNode(ctx, 0);
    envASR(g.gain, t, 0.18 * v, 0.004, dur * 0.55, dur * 0.45);
    g.connect(gtrPre);
    for (let i = 0; i < semis.length; i++) {
      for (let d = 0; d < 2; d++) {
        const o = ctx.createOscillator(); o.type = 'sawtooth';
        o.frequency.setValueAtTime(noteHz(root + semis[i], 110), t);
        o.detune.setValueAtTime((d ? 6 : -6) + (rnd() * 4 - 2), t);
        const og = gainNode(ctx, i === 3 ? 0.24 : 0.4);
        o.connect(og); og.connect(g); o.start(t); o.stop(t + dur + 0.2);
      }
    }
  }
  function leadNote(t, semi, dur, v) {
    const f = noteHz(semi, 220);
    const o = ctx.createOscillator(); o.type = 'sawtooth'; o.frequency.setValueAtTime(f, t);
    const o2 = ctx.createOscillator(); o2.type = 'square'; o2.frequency.setValueAtTime(f, t);
    o2.detune.setValueAtTime(8, t);
    const vib = ctx.createOscillator(); vib.type = 'sine'; vib.frequency.setValueAtTime(5.4, t);
    const vg = gainNode(ctx, 0); envASR(vg.gain, t, 18, dur * 0.5, dur * 0.2, dur * 0.3);
    vib.connect(vg); vg.connect(o.detune); vg.connect(o2.detune); vib.start(t); vib.stop(t + dur + 0.2);
    const lp = filter(ctx, 'lowpass', 3000, 3);
    sweep(lp.frequency, t, 5200, 2200, dur);
    const sh = ctx.createWaveShaper(); sh.curve = shaperCurve(5);
    const g = gainNode(ctx, 0); envASR(g.gain, t, 0.20 * v, 0.010, dur * 0.55, dur * 0.5);
    const mix = gainNode(ctx, 0.5);
    o.connect(mix); o2.connect(mix); mix.connect(sh); sh.connect(lp); lp.connect(g); g.connect(leadIn);
    o.start(t); o.stop(t + dur + 0.25); o2.start(t); o2.stop(t + dur + 0.25);
  }
  function erhuNote(t, semi, dur, v) {
    const f = noteHz(semi, 220);
    const o = ctx.createOscillator(); o.type = 'sawtooth';
    o.frequency.setValueAtTime(f * 0.94, t);
    o.frequency.linearRampToValueAtTime(f, t + 0.08);                  // portamento in
    const vib = ctx.createOscillator(); vib.type = 'sine'; vib.frequency.setValueAtTime(5.8, t);
    const vg = gainNode(ctx, 0); envASR(vg.gain, t, 26, dur * 0.4, dur * 0.3, dur * 0.3);
    vib.connect(vg); vg.connect(o.detune); vib.start(t); vib.stop(t + dur + 0.3);
    const bp = filter(ctx, 'bandpass', 1200, 1.1);
    const lp = filter(ctx, 'lowpass', 2600, 0.9);
    const g = gainNode(ctx, 0); envASR(g.gain, t, 0.22 * v, 0.09, dur * 0.5, dur * 0.6);
    o.connect(bp); bp.connect(lp); lp.connect(g); g.connect(L.erhu);
    o.start(t); o.stop(t + dur + 0.4);
    const sd = gainNode(ctx, 0.4); g.connect(sd); sd.connect(graph.verbIn);
  }
  function padChord(t, root, quality, dur, v) {
    const third = quality === 'm' ? 3 : 4;
    const semis = [0, third, 7, 12, 12 + third];
    const lp = filter(ctx, 'lowpass', 900, 0.9);
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.setValueAtTime(0.14, t);
    const lg = gainNode(ctx, 340); lfo.connect(lg); lg.connect(lp.frequency);
    lfo.start(t); lfo.stop(t + dur + 0.6);
    const g = gainNode(ctx, 0); envASR(g.gain, t, 0.10 * v, dur * 0.28, dur * 0.35, dur * 0.55);
    lp.connect(g); g.connect(L.pad);
    for (let i = 0; i < semis.length; i++) {
      for (let d = 0; d < 2; d++) {
        const o = ctx.createOscillator(); o.type = 'sawtooth';
        o.frequency.setValueAtTime(noteHz(root + semis[i], 110), t);
        o.detune.setValueAtTime((d ? 8 : -8) + (rnd() * 6 - 3), t);
        const og = gainNode(ctx, 0.3 / (1 + i * 0.3));
        o.connect(og); og.connect(lp); o.start(t); o.stop(t + dur + 0.6);
      }
    }
  }
  function hornStab(t, root, quality, dur, v) {
    const third = quality === 'm' ? 3 : 4;
    const semis = [0, third, 7];
    const lp = filter(ctx, 'lowpass', 1500, 1.4);
    sweep(lp.frequency, t, 700, 1900, 0.09);
    const g = gainNode(ctx, 0); envASR(g.gain, t, 0.16 * v, 0.05, dur * 0.5, dur * 0.5);
    lp.connect(g); g.connect(L.horns);
    for (let i = 0; i < semis.length; i++) {
      const o = ctx.createOscillator(); o.type = 'sawtooth';
      const f = noteHz(root + semis[i], 110) * 2;
      o.frequency.setValueAtTime(f * 0.97, t);
      o.frequency.linearRampToValueAtTime(f, t + 0.06);
      const og = gainNode(ctx, 0.34 / (1 + i * 0.25));
      o.connect(og); og.connect(lp); o.start(t); o.stop(t + dur + 0.3);
    }
  }

  /* ---------------------------------------------------------- sequencer -- */
  const M = {
    state: 'silence', pending: null, pendingAt: 0,
    startTime: 0, step: 0, started: false,
    bars: 0, notes: 0, steps: 0, resyncs: 0,
    bpm: BPM, barSeconds: BAR,
    levels: L,
    get names() { return NAMES; },
  };

  function applyState(name, when) {
    const S = STATES[name] || STATES.silence;
    M.state = name;
    const tc = name === 'musou' || name === 'victory' ? 0.12 : 0.55;
    for (const k of NAMES) L[k].gain.setTargetAtTime(S.L[k] ?? 0, when, tc);
  }

  /** Schedule everything that happens on absolute 16th-step `s` at time `t`. */
  function scheduleStep(s, t) {
    const S = STATES[M.state] || STATES.silence;
    const pat = PAT[S.pat] || PAT.none;
    const bar = Math.floor(s / 16) % 8;
    const k = s % 16;
    const prog = PROG[S.prog] || PROG.main;
    const [root, quality] = prog[bar];
    const sparse = S.sparse || 1;
    const oct = S.octave || 0;
    const scale = S.major ? PENT_MAJ : PENT_MIN;
    const motif = S.major ? motifMajor : motifMinor;

    // --- drums
    if (S.L.drums > 0.001 || S.L.taiko > 0.001) {
      if (pat.kick.includes(k)) kick(t, k === 0 ? 1 : 0.82);
      if (pat.snare.includes(k)) snare(t, k === 4 || k === 12 ? 1 : 0.55);
      if (pat.hat.includes(k)) hat(t, k % 4 === 0 ? 1 : 0.6, k === 14 && bar % 2 === 1);
      if (pat.taiko.includes(k)) taikoHit(t, k === 0 ? 1 : 0.7);
      if (k === 0 && bar % 4 === 0) crash(t, 0.9);
    }
    // --- bass: driving 8ths with a pickup into the bar
    if (S.L.bass > 0.001 && k % 2 === 0 && (bar % sparse === 0)) {
      const oc = (k === 14) ? 12 : 0;
      bassNote(t, root + oc, STEP * 1.9, k === 0 ? 1 : 0.78);
    }
    // --- guitar: palm-muted 16ths, open chord on the bar
    if (S.L.guitar > 0.001) {
      if (k === 0) powerChord(t, root, BEAT * 1.9, 1, null);
      else if (k % 2 === 0 && k !== 8) powerChord(t, root, STEP * 0.85, 0.55, null);
      else if (k === 8) powerChord(t, root, BEAT * 0.9, 0.8, null);
    }
    // --- pad + horns, one chord per bar
    if (k === 0) {
      if (S.L.pad > 0.001) padChord(t, root - 12, quality, BAR * 0.98, 1);
      if (S.L.horns > 0.001 && (bar % 2 === 0 || M.state === 'victory' || M.state === 'musou')) {
        hornStab(t, root, quality, M.state === 'victory' ? BAR * 0.9 : BEAT * 1.1, 1);
      }
      M.bars++;
    }
    // --- lead
    if (S.L.lead > 0.001) {
      for (let i = 0; i < motif.length; i++) {
        const nt = motif[i];
        if (nt.bar === bar && nt.step === k) {
          leadNote(t, scale[Math.min(scale.length - 1, nt.deg)] + 12 + oct, nt.len * STEP * 0.92, nt.vel);
          M.notes++;
        }
      }
    }
    // --- erhu (exploring)
    if (S.L.erhu > 0.001) {
      for (let i = 0; i < erhuLine.length; i++) {
        const nt = erhuLine[i];
        if (nt.bar === bar && nt.step === k) { erhuNote(t, scale[nt.deg] + root, nt.len * STEP, 1); M.notes++; }
      }
    }
    M.steps++;
  }

  return {
    m: M,
    get state() { return M.state; },
    /** Queue a state; it lands on the next bar (next beat for urgent cues). */
    setState(name, urgent = false) {
      if (!STATES[name]) return false;
      if (name === M.state && !M.pending) return true;
      M.pending = name; M.pendingUrgent = urgent || name === 'musou' || name === 'victory';
      if (!M.started) { applyState(name, ctx.currentTime); M.pending = null; }
      return true;
    },
    start(when) {
      if (M.started) return;
      M.started = true;
      M.startTime = Math.max(ctx.currentTime + 0.08, when || 0);
      M.step = 0;
      applyState(M.state, M.startTime);
    },
    stop(fade = 0.6) {
      const now = ctx.currentTime;
      for (const k of NAMES) L[k].gain.setTargetAtTime(0, now, fade / 3);
      M.started = false;
    },
    /** Called once per rendered frame. Schedules the grid `LOOKAHEAD` ahead. */
    tick(now) {
      if (!M.started) return 0;
      let sched = 0;
      let t = M.startTime + M.step * STEP;
      // A stalled tab (or a capture that paused for a minute) must not try to
      // catch up thousands of steps: jump forward to the next bar line.
      if (t < now - 0.75) {
        const skip = Math.ceil((now - t) / STEP);
        M.step += skip + ((16 - ((M.step + skip) % 16)) % 16);
        M.resyncs++;
        t = M.startTime + M.step * STEP;
      }
      while (t < now + LOOKAHEAD && sched < 64) {
        if (M.pending && (M.step % 16 === 0 || (M.pendingUrgent && M.step % 4 === 0))) {
          applyState(M.pending, t); M.pending = null;
        }
        scheduleStep(M.step, t);
        M.step++; sched++;
        t = M.startTime + M.step * STEP;
      }
      return sched;
    },
    stats() {
      const lv = {};
      for (const k of NAMES) lv[k] = +L[k].gain.value.toFixed(3);
      return { state: M.state, pending: M.pending, bars: M.bars, steps: M.steps, notes: M.notes,
        resyncs: M.resyncs, bpm: BPM, levels: lv };
    },
  };
}
